/**
 * /faq — Frequently Asked Questions
 * ───────────────────────────────────────────────────────────────────────────
 * Public, indexable, prerendered. Renders 10 venue FAQs visibly (Accordion)
 * AND emits matching FAQPage JSON-LD via <FAQSchema /> so Google's Rich
 * Results / AI Overviews / Perplexity can surface them verbatim.
 *
 * IMPORTANT — content review:
 *   Answers marked with `needs_review: true` use the best info available
 *   from the venue's existing site copy + a placeholder where specifics
 *   like minimum spend, parking exact details, etc. weren't documented.
 *   The Footer / About page should remain the source of truth — once the
 *   venue confirms those numbers, swap the placeholders below and re-build.
 *
 * Schema-content matching:
 *   Google penalises FAQ schema where the JSON-LD answers don't match the
 *   visible page text. The FAQS constant below is the single source of
 *   truth — both the schema and the Accordion render from it, so they
 *   cannot drift.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import { FAQSchema, BreadcrumbSchema } from '../components/seo/SchemaMarkup';
import { ChevronLeft, HelpCircle, MapPin, Phone, MessageCircle } from 'lucide-react';

// ── Single source of truth ────────────────────────────────────────────
// Tweak answers in one place; the visible accordion and the JSON-LD both
// pull from this array.
const FAQS = [
  {
    id: 'age',
    question: 'What is the age policy at Scratch Unleashed?',
    answer:
      'Scratch Unleashed is a 21+ venue. All guests must carry a valid government-issued photo ID (Aadhaar, PAN, driving licence, or passport) for entry. Entry will be politely declined without verifiable ID, irrespective of appearance.',
    needs_review: false,
  },
  {
    id: 'walkin',
    question: 'Do you accept walk-ins or do I need a reservation?',
    answer:
      'Walk-ins are welcome subject to capacity, but on weekends and event nights the club regularly hits capacity by 10 PM. To guarantee entry we strongly recommend booking a pass on our /events page or reserving a VIP table. Pre-booked guests have priority at the door.',
    needs_review: false,
  },
  {
    id: 'dress-code',
    question: 'Is there a dress code?',
    answer:
      'Yes — smart-casual to dressy. We ask guests to avoid shorts, slippers/flip-flops, beachwear, sleeveless vests for men, and any visibly stained or torn clothing. Door staff reserve the right to refuse entry to anyone not meeting the dress code on a given night.',
    needs_review: false,
  },
  {
    id: 'cover',
    question: 'Is there a cover charge?',
    answer:
      'Entry is via pre-paid passes for event nights, with the exact pass price (and any included redemption) shown on each event listing at /events. On non-event nights cover may be waived for early arrivals or table bookings. Pass prices typically range from ₹500 to ₹2,500 depending on the event.',
    needs_review: true, // ← please confirm typical pass-price range
  },
  {
    id: 'vip-tables',
    question: 'How do VIP table bookings work — is there a minimum spend?',
    answer:
      'VIP tables are bookable in advance via WhatsApp or by selecting a VIP pass on the relevant event page. Each table has a minimum spend (the entire amount is redeemable against food and drinks at the table; nothing is forfeited). Minimum spend starts at ₹10,000 for standard VIP tables and scales up for premium-zone or rooftop tables on peak nights. WhatsApp us at +91 78703 69666 for live availability.',
    needs_review: true, // ← please confirm exact minimum spend tiers
  },
  {
    id: 'parking',
    question: 'Is parking available?',
    answer:
      "Yes. Scratch Unleashed is on the 3rd floor of Bindal Mall, Sonari, which offers mall parking on lower levels. Valet may be available on event nights — please ask at the entrance.",
    needs_review: true, // ← confirm valet hours / paid vs free
  },
  {
    id: 'hours',
    question: 'What are your operating hours?',
    answer:
      'Open Friday through Sunday from 6:00 PM until late, with doors typically closing around 1:30 AM. Weekdays (Monday–Thursday) are reserved for private events, corporate nights, and bespoke celebrations — contact us via WhatsApp to enquire.',
    needs_review: false,
  },
  {
    id: 'private-events',
    question: 'Can I book the venue for a private event or birthday?',
    answer:
      "Absolutely — weekday slots (Mon–Thu) are our private-event window. We host birthdays, anniversaries, corporate nights, brand activations, and bespoke celebrations. Send us a WhatsApp at +91 78703 69666 with your date, guest count, and rough idea of the vibe, and we'll come back with a customised quote within 24 hours.",
    needs_review: false,
  },
  {
    id: 'location',
    question: 'Where exactly is Scratch Unleashed located?',
    answer:
      'Scratch Unleashed is on the 3rd floor of Bindal Mall, Sonari, Jamshedpur (831011), Jharkhand, India. The mall is well-connected to Sakchi, Bistupur, and Mango. Look for the rooftop signage on the Bindal Mall facade.',
    needs_review: false,
  },
  {
    id: 'safety',
    question: 'How do you keep guests safe — and what about drinking & driving?',
    answer:
      "Guest safety is non-negotiable. We have trained bouncers and CCTV across every floor, and we run a strict 'no drugs, no drink-and-drive' policy displayed at the door. We'll always help you arrange a cab home — just ask at the bar or the entrance and we'll book one for you.",
    needs_review: false,
  },
];

const BREADCRUMBS = [
  { name: 'Home', url: 'https://scratchjsr.com/' },
  { name: 'FAQ',  url: 'https://scratchjsr.com/faq' },
];

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F2F2F2]">
      {/* ── SEO ─────────────────────────────────────────────────────── */}
      <Helmet>
        <title>FAQ | Scratch Unleashed — Jamshedpur Nightclub</title>
        <meta
          name="description"
          content="Frequently asked questions about Scratch Unleashed in Jamshedpur — age policy, dress code, VIP tables, parking, hours, private events, and more."
        />
        <link rel="canonical" href="https://scratchjsr.com/faq" />
        <meta property="og:title" content="FAQ | Scratch Unleashed" />
        <meta
          property="og:description"
          content="Everything you need to know before visiting Jamshedpur's premier nightclub — age, dress code, tables, parking, hours."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://scratchjsr.com/faq" />
      </Helmet>
      <FAQSchema faqs={FAQS} />
      <BreadcrumbSchema items={BREADCRUMBS} />

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 pb-12">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-[#B9B9B9] hover:text-[#F2F2F2] transition-colors"
          data-testid="faq-back-home"
        >
          <ChevronLeft size={14} /> Back to Home
        </Link>

        <div className="mt-8 mb-10 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#D4A843]/15 border border-[#D4A843]/30 mb-4">
            <HelpCircle className="text-[#D4A843]" size={26} />
          </div>
          <h1 className="font-['EB_Garamond'] text-4xl sm:text-5xl text-[#F2F2F2] font-bold tracking-tight">
            Frequently Asked Questions
          </h1>
          <p className="mt-3 text-[#B9B9B9] text-sm max-w-xl mx-auto leading-relaxed">
            Everything you need to know before your night at Scratch Unleashed — age policy,
            dress code, VIP tables, parking, hours, and how to book a private event.
          </p>
        </div>

        {/* ── Accordion ─────────────────────────────────────────────── */}
        <Accordion
          type="single"
          collapsible
          className="space-y-3"
          data-testid="faq-accordion"
        >
          {FAQS.map((faq) => (
            <AccordionItem
              key={faq.id}
              value={faq.id}
              className="border border-[#1E1E1E] rounded-xl bg-[#0E0E0E] px-5 data-[state=open]:border-[#D4A843]/40 transition-colors"
              data-testid={`faq-item-${faq.id}`}
            >
              <AccordionTrigger
                className="text-left text-[#F2F2F2] font-medium text-base py-4 hover:no-underline"
                data-testid={`faq-trigger-${faq.id}`}
              >
                {faq.question}
              </AccordionTrigger>
              <AccordionContent
                className="text-[#B9B9B9] text-sm leading-relaxed pb-5"
                data-testid={`faq-answer-${faq.id}`}
              >
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* ── Still have questions? CTA ─────────────────────────────── */}
        <div className="mt-12 border border-[#1E1E1E] rounded-xl p-6 bg-[#0E0E0E]">
          <h2 className="font-['EB_Garamond'] text-2xl text-[#F2F2F2]">Still have a question?</h2>
          <p className="mt-2 text-sm text-[#B9B9B9]">
            We&rsquo;re happy to help — reach out and we&rsquo;ll get back to you fast.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href="https://wa.me/917870369666"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-medium text-white hover:brightness-110 transition"
              data-testid="faq-cta-whatsapp"
            >
              <MessageCircle size={16} /> WhatsApp +91 78703 69666
            </a>
            <a
              href="tel:+917870369666"
              className="inline-flex items-center gap-2 rounded-lg border border-[#2A2A2A] bg-transparent px-4 py-2.5 text-sm text-[#F2F2F2] hover:bg-[#161616] transition"
              data-testid="faq-cta-call"
            >
              <Phone size={16} /> Call us
            </a>
            <Link
              to="/events"
              className="inline-flex items-center gap-2 rounded-lg bg-[#D4A843] px-4 py-2.5 text-sm font-medium text-black hover:brightness-110 transition"
              data-testid="faq-cta-events"
            >
              See upcoming events
            </Link>
          </div>
          <p className="mt-5 flex items-center gap-2 text-xs text-[#6F6F6F]">
            <MapPin size={12} /> 3rd Floor, Bindal Mall, Sonari, Jamshedpur 831011
          </p>
        </div>
      </div>
    </div>
  );
}
